import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { DateSelectionValue } from '../date-selection';

export class CustomValidators {
  /**
   * Validates that the given control _possibly_ is a url (with or without protocol prefix)
   *
   * @see https://stackoverflow.com/questions/3809401/what-is-a-good-regular-expression-to-match-a-url
   */
  static looseUrl: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const regEx = /^(https?:\/\/)?((localhost:\d{2,5})|[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&/=]*))$/;
    return Validators.pattern(regEx)(control) && { looseUrl: true };
  };

  /**
   * Validates that the given control _possibly_ is a url (accepts localhost)
   *
   */
  static url: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) {
      return null;
    }
    try {
      const url = new URL(value.trim());
      const isValidProtocol = url.protocol === 'http:' || url.protocol === 'https:';
      const isLocalhost = url.hostname === 'localhost';
      const hasValidHostname = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(url.hostname);

      if (isValidProtocol && (isLocalhost || hasValidHostname)) {
        return null;
      }
    } catch {
      return { url: true };
    }

    return { url: true };
  };

  /**
   * Validates that the given control is not one of the given blacklisted values
   */
  static blacklisted(blacklist: any[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      return blacklist.indexOf(control.value) > -1 ? { blacklisted: true } : null;
    };
  }

  /**
   * Validates that a password is secure enough
   */
  static passwordSecurity: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const tooShort = Validators.minLength(12)(control);
    const missesLetter = Validators.pattern('.*[a-zA-Z].*')(control);
    const missesNumber = Validators.pattern('.*[0-9].*')(control);
    if (tooShort && missesLetter && missesNumber) {
      return { passwordSecurityLengthLetterNumber: true };
    } else if (tooShort && missesLetter) {
      return { passwordSecurityLengthLetter: true };
    } else if (tooShort && missesNumber) {
      return { passwordSecurityLengthNumber: true };
    } else if (missesLetter && missesNumber) {
      return { passwordSecurityLetterNumber: true };
    } else if (tooShort) {
      return { passwordSecurityLength: true };
    } else if (missesLetter) {
      return { passwordSecurityLetter: true };
    } else if (missesNumber) {
      return { passwordSecurityNumber: true };
    }
    return null;
  };

  /**
   * Validates that a date is a valid date and optionally in the past
   */
  static futureDateValidator(): ValidatorFn {
    return (control: AbstractControl<DateSelectionValue>): ValidationErrors | null => {
      const value = control.value;
      if (value && value !== 'INVALID') {
        const date = new Date(value);
        if (date > new Date()) {
          return { futureDate: true };
        }
      }
      return null;
    };
  }

  /**
   * Validates that a human is not born before the year 1900
   */
  static oldHumanValidator(): ValidatorFn {
    return (control: AbstractControl<DateSelectionValue>): ValidationErrors | null => {
      const value = control.value;
      if (value && value !== 'INVALID') {
        const date = new Date(value);
        if (date < new Date('1900-01-01')) {
          return { oldHuman: true };
        }
      }
      return null;
    };
  }

  /**
   * Validates that the entered date is a valid date
   */
  static invalidDateValidator(): ValidatorFn {
    return (control: AbstractControl<DateSelectionValue>): ValidationErrors | null => {
      return control.value === 'INVALID' ? { invalidDate: true } : null;
    };
  }

  /**
   * Validates that the entered code is a valid zip code for the given country.
   * Currently only supports Germany (DE) with a 5-digit zip code format.
   */
  static invalidZipCodeValidator(forCountry: string): ValidatorFn {
    return (control: AbstractControl<string>): ValidationErrors | null => {
      const value = control.value;
      if (!value) return null;

      if (forCountry === 'DE') {
        const isValid = /^[0-9]{5}$/.test(value);
        return isValid ? null : { invalidZipCode: true };
      }

      return null;
    };
  }
}
